import { useEffect, useMemo, useState } from "react";
import { X, Gift, Sparkles, Coins, RotateCw, Clock, Check, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Professional "Select promotion" sheet.
 *   <SelectPromotionSheet open selectedId onClose onConfirm />
 * Mobile = bottom sheet, Desktop = centered modal.
 */
export default function SelectPromotionSheet({ open, selectedId, onClose, onConfirm }) {
  const [tab, setTab] = useState("valid");
  const [valid, setValid] = useState([]);
  const [invalid, setInvalid] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(selectedId || "");

  useEffect(() => { if (open) setPicked(selectedId || "__none__"); }, [open, selectedId]);

  useEffect(() => {
    if (!open) return;
    let aborted = false;
    setLoading(true);
    api
      .get("/promotions/available")
      .then((res) => {
        if (aborted) return;
        const d = res?.data || {};
        setValid(Array.isArray(d.valid) ? d.valid : []);
        setInvalid(Array.isArray(d.invalid) ? d.invalid : []);
      })
      .catch(() => { /* silent */ })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [open]);

  const list = tab === "valid" ? valid : invalid;

  const fmtDate = (s) => {
    if (!s) return "";
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return String(s);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return String(s); }
  };
  const rangeText = (p) => {
    const s = fmtDate(p.start_at);
    const e = fmtDate(p.end_at);
    if (!s && !e) return "Always available";
    return `${s || "—"} → ${e || "—"}`;
  };

  const pickedPromo = useMemo(
    () => [...valid, ...invalid].find((p) => p.id === picked) || null,
    [picked, valid, invalid],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center sm:justify-center bg-black/70 backdrop-blur-md animate-[fadeIn_.2s_ease]"
      data-testid="select-promo-sheet"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <style>{`
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes sheetUp{from{transform:translateY(24px);opacity:.6}to{transform:translateY(0);opacity:1}}
      `}</style>

      <div
        className="relative w-full sm:max-w-lg bg-[#0b131c] rounded-t-[26px] sm:rounded-[22px] border-t sm:border border-white/10 flex flex-col shadow-[0_-20px_60px_rgba(0,0,0,.6)] sm:shadow-[0_30px_80px_rgba(0,0,0,.7)] animate-[sheetUp_.28s_cubic-bezier(.2,.7,.2,1)]"
        style={{ maxHeight: "90vh" }}
      >
        {/* Grab handle (mobile) */}
        <div className="sm:hidden pt-3 flex justify-center">
          <span className="h-1.5 w-11 rounded-full bg-white/15" />
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-4 sm:pt-5 pb-4">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-gradient-to-br from-[#19a55c] to-[#0d7a41] shadow-lg shadow-emerald-900/40">
            <Gift size={20} className="text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-extrabold text-white leading-tight">Select promotion</h2>
            <p className="text-[12px] text-white/50">Pick a bonus for this deposit — or continue without one</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid place-items-center h-9 w-9 rounded-full bg-white/5 text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close"
            data-testid="select-promo-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5">
          <div className="flex gap-2 p-1 rounded-xl bg-white/[0.04] border border-white/8">
            {[
              { id: "valid", label: "Available", count: valid.length },
              { id: "invalid", label: "Expired", count: invalid.length },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`promo-tab-${t.id}`}
                className={`flex-1 py-2.5 rounded-lg text-[13.5px] font-bold tracking-wide transition-all ${
                  tab === t.id
                    ? "bg-[#19a55c] text-white shadow-md shadow-emerald-900/40"
                    : "text-white/55 hover:text-white/85"
                }`}
              >
                {t.label} · {t.count}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" style={{ overscrollBehavior: "contain" }}>
          {loading && (
            <div className="space-y-3">
              {[0, 1].map((i) => (
                <div key={i} className="h-24 rounded-2xl bg-white/[0.04] border border-white/8 animate-pulse" />
              ))}
            </div>
          )}

          {!loading && tab === "valid" && (
            <button
              type="button"
              onClick={() => setPicked("__none__")}
              data-testid="promo-card-none"
              className={`w-full text-left rounded-2xl border p-4 transition-all duration-200 flex items-center gap-3 ${
                picked === "__none__"
                  ? "border-white/40 bg-white/[0.06] ring-1 ring-white/20"
                  : "border-white/10 bg-[#111c27] hover:border-white/20 hover:bg-[#152230]"
              }`}
            >
              <span
                className={`inline-grid place-items-center h-5 w-5 rounded-full border-2 shrink-0 transition-all ${
                  picked === "__none__" ? "border-white bg-white" : "border-white/25"
                }`}
              >
                {picked === "__none__" && <Check size={12} className="text-[#0b131c]" strokeWidth={3} />}
              </span>
              <div>
                <div className="text-[16px] font-bold text-white leading-snug">No promotion (Normal)</div>
                <div className="text-[12.5px] text-white/55">Deposit without any bonus or turnover requirement</div>
              </div>
            </button>
          )}

          {!loading && list.length === 0 && (
            <div className="py-12 text-center">
              <div className="mx-auto mb-3 grid place-items-center h-14 w-14 rounded-2xl bg-white/[0.04] border border-white/8">
                <Gift size={24} className="text-white/30" />
              </div>
              <div className="text-white/50 text-sm font-medium">
                No {tab === "valid" ? "available" : "expired"} promotions
              </div>
            </div>
          )}

          {!loading && list.map((p) => {
            const selected = picked === p.id;
            const expired = tab === "invalid";
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => !expired && setPicked(p.id)}
                data-testid={`promo-card-${p.id}`}
                className={`group w-full text-left rounded-2xl border p-4 transition-all duration-200 relative overflow-hidden ${
                  selected
                    ? "border-[#19a55c] bg-gradient-to-br from-[#19a55c]/[0.12] to-transparent ring-1 ring-[#19a55c]/50"
                    : "border-white/10 bg-[#111c27] hover:border-white/20 hover:bg-[#152230]"
                } ${expired ? "opacity-55" : ""}`}
              >
                {/* Bonus % badge (right) */}
                {p.bonus_pct > 0 && (
                  <div className="absolute top-0 right-0">
                    <div className="px-3 py-1.5 rounded-bl-2xl bg-gradient-to-r from-[#19a55c] to-[#12864a] text-white font-extrabold text-[15px] leading-none tracking-tight shadow">
                      {p.bonus_pct}%
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3 pr-14">
                  {/* Radio */}
                  <span
                    className={`mt-0.5 inline-grid place-items-center h-5 w-5 rounded-full border-2 shrink-0 transition-all ${
                      selected ? "border-[#19a55c] bg-[#19a55c]" : "border-white/25 bg-transparent group-hover:border-white/40"
                    }`}
                  >
                    {selected && <Check size={12} className="text-white" strokeWidth={3} />}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="text-[16px] font-bold text-white leading-snug">{p.name}</div>
                    {(p.sub_title || p.description) && (
                      <div className="text-[12.5px] text-white/60 mt-0.5 line-clamp-2">{p.sub_title || p.description}</div>
                    )}

                    {/* Chips */}
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                      {p.min_deposit > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg bg-white/[0.06] text-white/75">
                          <Coins size={11} className="text-emerald-400" /> Min ৳{p.min_deposit}
                        </span>
                      )}
                      {p.cap > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg bg-white/[0.06] text-white/75">
                          <Sparkles size={11} className="text-emerald-400" /> Up to ৳{p.cap}
                        </span>
                      )}
                      {p.turnover_multiplier > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg bg-amber-400/10 text-amber-300">
                          <RotateCw size={11} /> {p.turnover_multiplier}x turnover
                        </span>
                      )}
                      {p.first_deposit_only && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg bg-sky-400/10 text-sky-300">
                          <ShieldCheck size={11} /> First deposit
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-1 text-[11.5px] text-white/40 mt-2.5">
                      <Clock size={11} /> {rangeText(p)}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Confirm bar */}
        <div className="px-5 pb-6 sm:pb-5 pt-3 border-t border-white/8 bg-[#0b131c]">
          {pickedPromo && pickedPromo.bonus_pct > 0 && (
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-[13px] text-white/55">Selected bonus</span>
              <span className="text-[14px] font-bold text-[#37d67a]">
                {pickedPromo.bonus_pct}%{pickedPromo.turnover_multiplier > 0 ? ` · ${pickedPromo.turnover_multiplier}x` : ""}
              </span>
            </div>
          )}
          {picked === "__none__" && (
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-[13px] text-white/55">Selected</span>
              <span className="text-[14px] font-bold text-white/80">No promotion</span>
            </div>
          )}
          <button
            type="button"
            disabled={!picked}
            onClick={() => onConfirm?.(picked === "__none__" ? null : pickedPromo)}
            data-testid="select-promo-confirm"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-[#19a55c] to-[#12924f] hover:from-[#16924f] hover:to-[#0e7f43] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-[15px] tracking-wide transition-all shadow-lg shadow-emerald-900/30 active:scale-[.99]"
          >
            {picked ? "Confirm" : "Select an option"}
          </button>
        </div>
      </div>
    </div>
  );
}
